/**
 * NEXUMA — WorkerScanScreen (faqja e check-in/check-out e punëtorit, mobile-first)
 *
 * Dy mënyra regjistrimi, të pandryshuara:
 *  1. Skanim i QR-së së kiosku — kamera lexon QR-në rrotulluese të kiosku
 *  2. Aksion direkt — check-in/out pa kiosk
 *
 * ── Çfarë verifikohet ────────────────────────────────────────────────────────
 * Ky sistem NUK përdor GPS apo geofence. Verifikimi real, sipas backend-it
 * (`/api/qr/phone/verify-scan` → `verifyKioskOTP` → `checkKioskReplay`), është:
 *   • kiosku është i regjistruar dhe aktiv           → INVALID_OTP
 *   • kodi njëpërdorimësh është brenda dritares 10s  → INVALID_OTP
 *   • ky QR nuk është përdorur më parë nga ti        → REPLAY_DETECTED
 *   • identiteti yt është i lidhur dhe aktiv         → NO_WORKER_PROFILE / WORKER_INACTIVE
 *
 * Identiteti vjen gjithmonë nga token-i i hyrjes, kurrë nga një `worker_id` i
 * dërguar nga ky ekran. Skanimi jep një biletë (`scanTicket`); fillimi i punës
 * shkon te `/api/qr/phone/start-work`, veprimet e tjera te
 * `/api/attendance/direct-action` — të dyja e kërkojnë biletën.
 * Ndërfaqja shpjegon pikërisht këto — dhe asgjë më shumë. Mos shto tekst për
 * lokacion derisa backend-i të mos ketë verifikim lokacioni.
 *
 * ── Gjendjet ────────────────────────────────────────────────────────────────
 * Rezultati vjen nga një makinë gjendjesh (`OUTCOMES`), jo nga kontrolli i
 * tekstit të mesazhit. Çdo gjendje ka titull, shpjegim dhe veprimin e duhur të
 * rikuperimit, që punëtori ta dijë gjithmonë nëse veprimi u regjistrua.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Button, CircularProgress, Stack, IconButton,
  TextField, MenuItem, Tabs, Tab, alpha, useTheme,
} from '@mui/material';
import LoginIcon         from '@mui/icons-material/Login';
import LogoutIcon        from '@mui/icons-material/Logout';
import CoffeeIcon        from '@mui/icons-material/Coffee';
import PlayArrowIcon     from '@mui/icons-material/PlayArrow';
import QrCode2Icon       from '@mui/icons-material/QrCode2';
import PersonIcon        from '@mui/icons-material/Person';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import CameraAltIcon     from '@mui/icons-material/CameraAlt';
import StopIcon          from '@mui/icons-material/Stop';
import CheckCircleRoundedIcon   from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon  from '@mui/icons-material/ErrorOutlineRounded';
import CloudOffRoundedIcon      from '@mui/icons-material/CloudOffRounded';
import ReplayRoundedIcon        from '@mui/icons-material/ReplayRounded';
import ShieldOutlinedIcon       from '@mui/icons-material/ShieldOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import TimerOutlinedIcon        from '@mui/icons-material/TimerOutlined';
import BadgeOutlinedIcon        from '@mui/icons-material/BadgeOutlined';
import NoPhotographyOutlinedIcon from '@mui/icons-material/NoPhotographyOutlined';
import DoneAllRoundedIcon       from '@mui/icons-material/DoneAllRounded';
import HomeWorkOutlinedIcon     from '@mui/icons-material/HomeWorkOutlined';
import globals from '../../services/globals';
import { getAuthHeaders } from '../../services/authSession';

const API_URL = process.env.REACT_APP_API_URL || '';

/* ── Makina e gjendjeve të rezultatit ────────────────────────────────────────
 * `tone` përcakton ngjyrën, `Icon` formën, `recover` veprimin e ofruar. Asnjë
 * gjendje nuk mbështetet vetëm te ngjyra. `recorded` thotë qartë nëse veprimi
 * shkoi në databazë — kjo lexohet nga punëtori si fjali, kurrë nuk nënkuptohet.
 */
const OUTCOMES = {
  success: {
    tone: 'success', Icon: CheckCircleRoundedIcon, recorded: true,  recover: null,
    titleKey: 'workerScan.outcome.success.title',  bodyKey: 'workerScan.outcome.success.body',
  },
  expired: {
    tone: 'warning', Icon: TimerOutlinedIcon, recorded: false, recover: 'rescan',
    titleKey: 'workerScan.outcome.expired.title',  bodyKey: 'workerScan.outcome.expired.body',
  },
  invalidQr: {
    tone: 'warning', Icon: QrCode2Icon, recorded: false, recover: 'rescan',
    titleKey: 'workerScan.outcome.invalidQr.title', bodyKey: 'workerScan.outcome.invalidQr.body',
  },
  duplicate: {
    tone: 'info', Icon: ReplayRoundedIcon, recorded: false, recover: 'rescan',
    titleKey: 'workerScan.outcome.duplicate.title', bodyKey: 'workerScan.outcome.duplicate.body',
  },
  finished: {
    tone: 'neutral', Icon: DoneAllRoundedIcon, recorded: false, recover: 'dismiss',
    titleKey: 'workerScan.outcome.finished.title', bodyKey: 'workerScan.outcome.finished.body',
  },
  identity: {
    tone: 'warning', Icon: BadgeOutlinedIcon, recorded: false, recover: 'dismiss',
    titleKey: 'workerScan.outcome.identity.title', bodyKey: 'workerScan.outcome.identity.body',
  },
  rateLimited: {
    tone: 'warning', Icon: TimerOutlinedIcon, recorded: false, recover: 'rescan',
    titleKey: 'workerScan.outcome.rateLimited.title', bodyKey: 'workerScan.outcome.rateLimited.body',
  },
  offline: {
    tone: 'neutral', Icon: CloudOffRoundedIcon, recorded: false, recover: 'retry',
    titleKey: 'workerScan.outcome.offline.title', bodyKey: 'workerScan.outcome.offline.body',
  },
  cameraBlocked: {
    tone: 'warning', Icon: NoPhotographyOutlinedIcon, recorded: false, recover: 'dismiss',
    titleKey: 'workerScan.outcome.cameraBlocked.title', bodyKey: 'workerScan.outcome.cameraBlocked.body',
  },
  cameraMissing: {
    tone: 'warning', Icon: NoPhotographyOutlinedIcon, recorded: false, recover: 'dismiss',
    titleKey: 'workerScan.outcome.cameraMissing.title', bodyKey: 'workerScan.outcome.cameraMissing.body',
  },
  // Refuzim i punës nga distanca (pa leje sot, sesion i zyrës, kanal i fikur…).
  // Shpjegimi i saktë vjen nga serveri te `detail`.
  remoteDenied: {
    tone: 'warning', Icon: HomeWorkOutlinedIcon, recorded: false, recover: 'dismiss',
    titleKey: 'workerScan.outcome.remoteDenied.title', bodyKey: 'workerScan.outcome.remoteDenied.body',
  },
  verifyError: {
    tone: 'error', Icon: ErrorOutlineRoundedIcon, recorded: false, recover: 'retry',
    titleKey: 'workerScan.outcome.verifyError.title', bodyKey: 'workerScan.outcome.verifyError.body',
  },
};

/** Kodet e backend-it → gjendje e ndërfaqes. Çdo kod i panjohur bie te verifyError. */
const ERROR_TO_OUTCOME = {
  INVALID_KIOSK_OTP:  'expired',
  INVALID_OTP:        'expired',
  REPLAY_DETECTED:    'duplicate',
  ALREADY_SCANNED:    'duplicate',
  SESSION_FINISHED:   'finished',
  KIOSK_NOT_FOUND:    'invalidQr',
  KIOSK_NOT_ENROLLED: 'invalidQr',
  WORKER_NOT_FOUND:   'identity',
  WORKER_INACTIVE:    'identity',
  WORKER_NOT_ELIGIBLE: 'identity',
  NOT_ENROLLED:       'identity',
  NO_WORKER_PROFILE:  'identity',
  CONFIRM_EXPIRED:    'expired',
  SCAN_REQUIRED:      'expired',
  RATE_LIMITED:       'rateLimited',
  SERVER_ERROR:       'verifyError',
};

const outcomeFor = (code) => ERROR_TO_OUTCOME[String(code || '').toUpperCase()] || 'verifyError';

/**
 * Kanali i telefonit është i fikur për këtë punëtor.
 *
 * Një gjendje e vetën, jo një buton i çaktivizuar: një buton gri e lë njeriun
 * të provojë dhe të mos kuptojë pse s'ndodh asgjë. Këtu thuhet hapur çka nuk
 * lejohet dhe ku regjistrohet prezenca në vend të tij.
 */
function ChannelDisabledNotice({ t }) {
  return (
    <Box role="status" sx={{ textAlign: 'center', py: 4, px: 2 }}>
      <Box
        sx={{
          width: 84, height: 84, borderRadius: '50%', mx: 'auto', mb: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'grey.100', color: 'text.disabled',
        }}
      >
        <NoPhotographyOutlinedIcon sx={{ fontSize: 38 }} />
      </Box>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, mb: 0.75 }}>
        {t('workerScan.channelOff.title', 'Regjistrimi nga telefoni nuk është i lejuar')}
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', px: 1 }}>
        {t('workerScan.channelOff.body', 'Prezenca jote regjistrohet te kioska ose nga ora e punës në web. Për ndryshim, kontakto HR-në.')}
      </Typography>
    </Box>
  );
}

/* ── Puna nga distanca ───────────────────────────────────────────────────────
 * Shfaqet VETËM kur serveri thotë se sot ka leje (`GET /api/attendance/remote`
 * → `allowed`). Në çdo ditë tjetër nuk montohet fare: ky ekran mbetet ai i
 * kioskut. Verdikti është i serverit — `POST` e kontrollon sërish lejen,
 * kanalin WEB dhe gjendjen e ditës, ndaj butoni këtu është vetëm mirësjellje.
 */
const REMOTE_STATE_KEY = {
  not_started: 'workerScan.remote.stateNotStarted',
  working:     'workerScan.remote.stateWorking',
  on_break:    'workerScan.remote.stateOnBreak',
  finished:    'workerScan.remote.stateFinished',
};

function RemoteWorkPanel({ remote, actions, busy, online, onAction, t }) {
  const theme = useTheme();
  const buttons = actions.filter((b) => remote.allowed_actions?.includes(b.action));
  return (
    <Box
      component="section" aria-labelledby="remote-work-title"
      sx={{
        p: 2, mb: 2, borderRadius: 3, bgcolor: 'background.paper',
        border: 1, borderColor: alpha(theme.palette.primary.main, 0.3),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
        <HomeWorkOutlinedIcon sx={{ color: 'primary.main' }} />
        <Typography id="remote-work-title" sx={{ fontWeight: 800, fontSize: '0.98rem' }}>
          {t('workerScan.remote.title', 'Punë nga distanca sot')}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: buttons.length ? 1.5 : 0 }}>
        {remote.code === 'REMOTE_CLOSE_ONLY'
          ? t('workerScan.remote.closeOnly', 'Leja për sot u tërhoq. Mund ta mbyllësh punën që nise nga distanca.')
          : t(REMOTE_STATE_KEY[remote.state] || REMOTE_STATE_KEY.not_started)}
        {remote.permit?.approved_by && remote.code !== 'REMOTE_CLOSE_ONLY' && (
          <> · {t('workerScan.remote.approvedBy', { who: remote.permit.approved_by, defaultValue: 'Lejoi: {{who}}' })}</>
        )}
      </Typography>
      {buttons.length > 0 && (
        <Stack spacing={1}>
          {buttons.map((b) => (
            <Button
              key={b.action} variant="contained" fullWidth
              disabled={busy || !online}
              onClick={() => onAction(b.action)}
              startIcon={!busy ? b.icon : undefined}
              sx={{
                bgcolor: b.color, color: '#fff',
                '&:hover': { bgcolor: b.color, filter: 'brightness(0.9)' },
                textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.25, fontSize: '0.92rem',
              }}
            >
              {busy ? <CircularProgress size={18} color="inherit" /> : (b.remoteLabel || b.label)}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/* ── Paneli i rezultatit ─────────────────────────────────────────────────────
 * Një panel për çdo gjendje: ikonë + titull + shpjegim + a u regjistrua + dalje.
 * `role="alert"` për gabimet dhe `role="status"` për sukses, që lexuesit e
 * ekranit ta njoftojnë menjëherë.
 */
function OutcomePanel({ outcomeKey, detail, onRescan, onRetry, onDismiss, t }) {
  const theme = useTheme();
  const o = OUTCOMES[outcomeKey] || OUTCOMES.verifyError;
  const paletteKey = o.tone === 'neutral' ? null : o.tone;
  const accent = paletteKey ? theme.palette[paletteKey].main : theme.palette.grey[500];
  const fg = paletteKey ? theme.palette[paletteKey].dark : theme.palette.text.secondary;
  const bg = paletteKey ? theme.palette[paletteKey].soft : theme.palette.grey[100];
  const Icon = o.Icon;

  return (
    <Box
      role={o.tone === 'success' ? 'status' : 'alert'}
      aria-live={o.tone === 'success' ? 'polite' : 'assertive'}
      sx={{
        mt: 2, p: 2, borderRadius: 3,
        bgcolor: bg, border: `1px solid ${alpha(accent, 0.35)}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(accent, 0.16), color: accent,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: fg, lineHeight: 1.3 }}>
            {t(o.titleKey)}
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.5 }}>
            {t(o.bodyKey)}
          </Typography>

          {/* Detaji nga serveri (emri i kiosku, ora e regjistruar) — nëse ka. */}
          {detail && (
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.75, fontStyle: 'italic' }}>
              {detail}
            </Typography>
          )}

          {/* Fjalia që s'guxon të mungojë: a u regjistrua veprimi apo jo. */}
          <Stack direction="row" spacing={0.625} alignItems="center" sx={{ mt: 1 }}>
            {o.recorded
              ? <CheckCircleRoundedIcon sx={{ fontSize: 15, color: theme.palette.success.main }} />
              : <ErrorOutlineRoundedIcon sx={{ fontSize: 15, color: theme.palette.text.disabled }} />}
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: o.recorded ? theme.palette.success.dark : 'text.secondary' }}>
              {t(o.recorded ? 'workerScan.recorded' : 'workerScan.notRecorded')}
            </Typography>
          </Stack>

          {o.recover && (
            <Button
              size="small"
              variant="outlined"
              startIcon={o.recover === 'rescan' ? <CameraAltIcon /> : <ReplayRoundedIcon />}
              onClick={o.recover === 'rescan' ? onRescan : o.recover === 'retry' ? onRetry : onDismiss}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
            >
              {t(`workerScan.recover.${o.recover}`)}
            </Button>
          )}
        </Box>

        <IconButton size="small" onClick={onDismiss} aria-label={t('workerScan.dismiss')}>
          <StopIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    </Box>
  );
}

/* ── Shpjegimi i verifikimit ─────────────────────────────────────────────────
 * Transparencë, jo mbikëqyrje: punëtori e sheh saktësisht çfarë kontrollohet,
 * një herë, para veprimit — jo një ndjesi monitorimi të vazhdueshëm.
 */
function VerificationNote({ t }) {
  const theme = useTheme();
  const checks = [
    { Icon: ShieldOutlinedIcon,       key: 'kiosk' },
    { Icon: TimerOutlinedIcon,        key: 'otp' },
    { Icon: VerifiedUserOutlinedIcon, key: 'identity' },
  ];
  return (
    <Box
      sx={{
        p: 1.75, borderRadius: 3, bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
        {t('workerScan.verify.title')}
      </Typography>
      <Stack spacing={0.875}>
        {checks.map(({ Icon, key }) => (
          <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
            <Icon sx={{ fontSize: 16, color: 'primary.main', mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.45 }}>
              {t(`workerScan.verify.${key}`)}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', mt: 1.25, lineHeight: 1.45 }}>
        {t('workerScan.verify.privacy')}
      </Typography>
    </Box>
  );
}

/* ── Kartela e identifikimit ─────────────────────────────────────────────────── */
function IdentityPicker({ workers, value, onChange, t }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: 'background.paper', borderRadius: 3, p: 2, mb: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
          {t('workerScan.identifyFirst')}
        </Typography>
      </Stack>
      <TextField
        select fullWidth size="small"
        label={t('workerScan.selectWorker')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {workers.map((w) => (
          <MenuItem key={w.id} value={String(w.id)}>
            {w.first_name} {w.last_name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

export default function WorkerScanScreen({ onBack }) {
  const { t } = useTranslation();
  const theme = useTheme();

  // Veprimet e lejuara — ngjyrat vijnë nga tema, jo nga hex-e të shpërndara.
  const ACTIONS = useMemo(() => ([
    { action: 'checkin',     label: t('kiosk.actionCheckin'),    icon: <LoginIcon />,     color: theme.palette.success.main,
      remoteLabel: t('workerScan.remote.start', 'Fillo punën nga distanca') },
    { action: 'break_start', label: t('kiosk.actionBreakStart'), icon: <CoffeeIcon />,    color: theme.palette.warning.main },
    { action: 'break_end',   label: t('kiosk.actionBreakEnd'),   icon: <PlayArrowIcon />, color: theme.palette.info.main },
    { action: 'checkout',    label: t('kiosk.actionCheckout'),   icon: <LogoutIcon />,    color: theme.palette.grey[800] },
  ]), [t, theme]);

  const [phase,      setPhase]      = useState('init'); // init | ready | error
  const [workerId,   setWorkerId]   = useState(null);
  const [workerName, setWorkerName] = useState('');
  const [allWorkers, setAllWorkers] = useState([]);
  const [errorMsg,   setErrorMsg]   = useState('');

  const [tab, setTab] = useState(0); // 0 = skanim, 1 = aksion direkt
  const [pickedWorker, setPickedWorker] = useState('');

  // Rezultati i çdo veprimi — { key, detail }. Një burim i vetëm i së vërtetës.
  const [outcome, setOutcome] = useState(null);
  const [scanning,    setScanning]    = useState(false);
  const [scanResult,  setScanResult]  = useState(null); // { k, t } pas skanimit
  const [busy,        setBusy]        = useState(false);
  const [workerState, setWorkerState] = useState(null); // zgjedhja kur je "working"

  // Lidhja — veprimet bllokohen offline, që të mos krijohet përshtypja e rreme
  // se diçka u ruajt. Sistemi nuk ka radhë sinkronizimi offline.
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));

  // A lejohet fare ky kanal për këtë punëtor? Verdikti është i SERVERIT — ky
  // ekran e pasqyron, nuk e vendos. `null` = ende s'dihet, ndaj asgjë nuk
  // fshihet para se përgjigjja të mbërrijë (përndryshe butonat do të pulsonin).
  const [mobileAllowed, setMobileAllowed] = useState(null);

  // Puna nga distanca — `null` derisa të dihet; paneli shfaqet vetëm me `allowed`.
  const [remote, setRemote] = useState(null);

  const html5QrRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  useEffect(() => () => { mounted.current = false; }, []);

  // ── Identifiko userin aktiv nga sesioni / DB (logjikë e pandryshuar) ────────
  useEffect(() => {
    async function init() {
      try {
        const user = globals().getLogedInUser();
        const userId = user?.data?.id;

        let linkedWid = user?.data?.worker_id;
        let userEmail = user?.data?.email || user?.data?.username || '';
        let userName  = [user?.data?.firstname, user?.data?.lastname].filter(Boolean).join(' ') || '';

        if (userId && (!linkedWid || !userEmail)) {
          try {
            const resp = await fetch(`${API_URL}/api/users/${userId}`, { headers: getAuthHeaders() });
            const data = await resp.json();
            if (data?.success && data?.data) {
              const dbUser = Array.isArray(data.data) ? data.data[0] : data.data;
              if (!linkedWid) linkedWid = dbUser?.worker_id;
              if (!userEmail) userEmail = dbUser?.email || '';
              if (!userName)  userName  = [dbUser?.firstname, dbUser?.lastname].filter(Boolean).join(' ') || '';
            }
          } catch { /* vazhdo me atë që kemi */ }
        }

        if (linkedWid) {
          setWorkerId(String(linkedWid));
          setWorkerName(userName);
          setPhase('ready');
          return;
        }

        // Pa worker_id — provo auto-match me email, përndryshe ofro picker.
        try {
          const wResp = await fetch(`${API_URL}/api/qr/workers/list`, { headers: getAuthHeaders() });
          const wData = await wResp.json();
          const workers = wData?.success ? (wData.workers || []) : [];
          const emailLower = userEmail.trim().toLowerCase();
          const matched = emailLower
            ? workers.find((w) => (w.email || '').trim().toLowerCase() === emailLower)
            : null;

          if (matched) {
            setWorkerId(String(matched.id));
            setWorkerName([matched.first_name, matched.last_name].filter(Boolean).join(' ') || userName);
          } else {
            setAllWorkers(workers);
          }
        } catch { /* picker mbetet bosh; aksionet qëndrojnë të bllokuara */ }
        setPhase('ready');
        setTab(0);
      } catch {
        setErrorMsg(t('workerScan.initError'));
        setPhase('error');
      }
    }
    init();
  }, [t]);

  // ── Kamera ─────────────────────────────────────────────────────────────────
  const stopCamera = useCallback(async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); } catch { /* tashmë e ndalur */ }
      try { html5QrRef.current.clear(); } catch { /* tashmë e pastruar */ }
      html5QrRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  /* Kanali MOBILE, i lexuar nga sesioni aktiv (server.js e kthen te
   * `attendance_allowed`). Fshehja e butonave këtu është vetëm mirësjellje:
   * `/api/qr/phone/*` dhe `/api/attendance/direct-action` e refuzojnë veprimin
   * edhe nëse dikush e thërret drejtpërdrejt. Të dyja duhen. */
  useEffect(() => {
    if (!workerId) { setMobileAllowed(null); return undefined; }
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/attendance/active-session?worker_id=${workerId}`, {
          headers: getAuthHeaders(),
        });
        const j = await r.json().catch(() => ({}));
        if (!alive) return;
        // Në dyshim lejo: një gabim leximi s'duhet ta ndalojë punën. Serveri
        // mbetet gardhi i vërtetë.
        const allowed = j?.data?.attendance_allowed;
        setMobileAllowed(allowed && typeof allowed === 'object' ? allowed.MOBILE !== false : true);
      } catch { if (alive) setMobileAllowed(true); }
    })();
    return () => { alive = false; };
  }, [workerId]);

  /* Leja e sotme për punë nga distanca. Identiteti vjen nga token-i, jo nga
   * `workerId` i këtij ekrani — kush zgjedh një punëtor tjetër te picker-i nuk
   * merr lejen e tij. Në dyshim fsheh: pa përgjigje, ekrani mbetet ai i kioskut. */
  const loadRemote = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/attendance/remote?channel=WEB`, { headers: getAuthHeaders() });
      const j = await r.json().catch(() => null);
      if (mounted.current) setRemote(j?.success ? j : null);
    } catch { if (mounted.current) setRemote(null); }
  }, []);

  useEffect(() => { if (workerId) loadRemote(); }, [workerId, loadRemote]);

  const handleRemoteAction = async (action) => {
    if (!online) { setOutcome({ key: 'offline' }); return; }
    setBusy(true);
    setOutcome(null);
    try {
      const r = await fetch(`${API_URL}/api/attendance/remote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ action, channel: 'WEB' }),
      }).then((res) => res.json());
      if (r?.success) setOutcome({ key: 'success', detail: r.message || '' });
      else if (!r?.code) setOutcome({ key: 'verifyError', detail: r?.message || '' });
      else setOutcome({ key: r.code === 'SESSION_FINISHED' ? 'finished' : 'remoteDenied', detail: r.message || '' });
    } catch {
      setOutcome({ key: navigator.onLine === false ? 'offline' : 'verifyError' });
    } finally {
      setBusy(false);
      loadRemote();
    }
  };

  const handleScanResponse = useCallback((r) => {
    if (r?.success) {
      if (r.needs_choice) { setWorkerState(r); setOutcome(null); return; }
      setOutcome({
        key: 'success',
        detail: [r.kiosk_name && t('workerScan.atKiosk', { kiosk: r.kiosk_name }), r.message]
          .filter(Boolean).join(' · '),
      });
      setScanResult(null);
      setWorkerState(null);
      return;
    }
    // `direct-action` e jep arsyen te `error` si fjali (p.sh. orari i pauzës),
    // jo si kod — shfaqe atë në vend të një gabimi të përgjithshëm.
    const sentence = /\s/.test(String(r?.error || '')) ? r.error : '';
    setOutcome({ key: outcomeFor(r?.error), detail: r?.message || sentence });
    // Një QR i skaduar/i përdorur nuk ripërdoret — pastro rezultatin e skanimit.
    setScanResult(null);
    setWorkerState(null);
  }, [t]);

  /** QR-ja e lexuar → bileta e skanimit. Identiteti vjen nga token-i. */
  const verifyScan = useCallback((scan) => fetch(`${API_URL}/api/qr/phone/verify-scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ qr: JSON.stringify({
      ...(scan.c ? { v: 2, c: scan.c } : {}), k: scan.k, t: scan.t,
    }) }),
  }).then((res) => res.json()), []);

  /** Një veprim prezence me biletën e skanimit (pauzë, kthim, dalje). */
  const ticketAction = useCallback((action, ticket) => fetch(`${API_URL}/api/attendance/direct-action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ worker_id: workerId, action, scan_ticket: ticket }),
  }).then((res) => res.json()), [workerId]);

  /**
   * Skanimi i kioskut, nga fillimi në fund:
   *   verify-scan → bileta
   *   start-work me biletën → puna fillon (orari, lokacioni, kanali si te app-i)
   *   ALREADY_ACTIVE → në pauzë: kthim automatik; duke punuar: zgjedhje
   */
  const scanAndAct = useCallback(async (scan) => {
    const v = await verifyScan(scan);
    if (!v?.success || !v.scanTicket) {
      setOutcome({ key: outcomeFor(v?.code || v?.error), detail: v?.message || '' });
      setScanResult(null);
      return;
    }
    const kioskName = v.kioskName || '';
    setScanResult({ ...scan, ticket: v.scanTicket, kioskName });

    const r = await fetch(`${API_URL}/api/qr/phone/start-work`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ scan_ticket: v.scanTicket }),
    }).then((res) => res.json());
    if (r?.code !== 'ALREADY_ACTIVE') {
      handleScanResponse({ ...r, error: r?.code, kiosk_name: kioskName });
      return;
    }

    // Puna është hapur — gjendja vjen nga sesioni i sotëm.
    const s = await fetch(`${API_URL}/api/attendance/active-session?worker_id=${workerId}`, {
      headers: getAuthHeaders(),
    }).then((res) => res.json()).catch(() => null);
    const session = s?.data?.session || null;
    const breaks = Array.isArray(session?.breaks) ? session.breaks : [];
    const last = breaks[breaks.length - 1];
    if (last && last.start && !last.end) {
      const b = await ticketAction('break_end', v.scanTicket);
      handleScanResponse({ ...b, kiosk_name: kioskName });
      return;
    }
    const closedMin = breaks.reduce((sum, b) => sum + (b.start && b.end
      ? Math.round((new Date(b.end) - new Date(b.start)) / 60000) : 0), 0);
    const sinceIn = session?.check_in
      ? Math.round((Date.now() - new Date(session.check_in)) / 60000) : 0;
    handleScanResponse({
      success: true, needs_choice: true,
      worker_name: r.workerName || workerName,
      kiosk_name: kioskName,
      allowed_actions: ['break_start', 'checkout'],
      work_minutes_so_far: Math.max(0, sinceIn - closedMin),
    });
  }, [verifyScan, ticketAction, handleScanResponse, workerId, workerName]);

  const startCamera = useCallback(async () => {
    if (!online) { setOutcome({ key: 'offline' }); return; }
    setOutcome(null);
    setScanResult(null);
    setWorkerState(null);
    setScanning(true);

    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
    } catch (permErr) {
      setScanning(false);
      setOutcome({
        key: permErr?.name === 'NotAllowedError' ? 'cameraBlocked' : 'cameraMissing',
      });
      return;
    }

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      if (!document.getElementById('qr-reader')) {
        setScanning(false);
        setOutcome({ key: 'verifyError' });
        return;
      }

      const scanner = new Html5Qrcode('qr-reader', { verbose: false });
      html5QrRef.current = scanner;

      const onDecode = async (decodedText) => {
        let data;
        try { data = JSON.parse(decodedText); } catch { setOutcome({ key: 'invalidQr' }); return; }
        const k = data.k || data.kiosk_id;
        const otp = data.t || data.otp;
        if (!k || !otp) { setOutcome({ key: 'invalidQr' }); return; }

        await stopCamera();
        // `c` (kompania, QR v2) ruhet që serveri ta krahasojë me kompaninë e
        // sesionit — pa të QR-ja do të lexohej si e kësaj kompanie.
        setScanResult({ k, t: otp, ...(data.c ? { c: data.c } : {}) });

        // Punëtor i paidentifikuar — ruaj QR-në dhe kërko identifikim.
        if (!workerId) { setOutcome({ key: 'identity' }); return; }

        setBusy(true);
        try {
          await scanAndAct({ k, t: otp, ...(data.c ? { c: data.c } : {}) });
        } catch {
          setOutcome({ key: navigator.onLine === false ? 'offline' : 'verifyError' });
          setScanResult(null);
        } finally {
          setBusy(false);
        }
      };

      try {
        await scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 220, height: 220 } }, onDecode, () => {});
      } catch {
        await scanner.start({ facingMode: 'user' }, { fps: 10, qrbox: { width: 220, height: 220 } }, onDecode, () => {});
      }
    } catch {
      setScanning(false);
      setOutcome({ key: 'cameraMissing' });
    }
  }, [workerId, stopCamera, online, scanAndAct]);

  // ── Aksioni pas skanimit (kur punëtori zgjedh vetë) ────────────────────────
  const handleKioskAction = async (action) => {
    if (!scanResult?.ticket || !workerId) return;
    if (!online) { setOutcome({ key: 'offline' }); return; }
    setBusy(true);
    setOutcome(null);
    try {
      const r = await ticketAction(action, scanResult.ticket);
      handleScanResponse({ ...r, kiosk_name: scanResult.kioskName });
    } catch {
      setOutcome({ key: navigator.onLine === false ? 'offline' : 'verifyError' });
    } finally {
      setBusy(false);
    }
  };

  // ── Aksion direkt (pa kiosk) ───────────────────────────────────────────────
  const handleDirectAction = async (action) => {
    if (!workerId) { setOutcome({ key: 'identity' }); return; }
    if (!online) { setOutcome({ key: 'offline' }); return; }
    setBusy(true);
    setOutcome(null);
    try {
      // Token-i mungonte këtu — `direct-action` ka `requireAuth`, prandaj kjo
      // rrugë kthente 401 pa u vënë re, ndërsa thirrjet e tjera në këtë ekran
      // e dërgonin token-in si duhet.
      const auth = { 'Content-Type': 'application/json', ...getAuthHeaders() };

      // Nëse QR-ja e kioskut sapo u lexua, ajo e provon praninë: shndërrohet
      // në biletën që kërkon serveri për çdo veprim prezence.
      let scanTicket = scanResult?.ticket;
      if (!scanTicket && scanResult?.k && scanResult?.t) {
        const v = await verifyScan(scanResult).catch(() => null);
        if (v?.code === 'WRONG_COMPANY') {
          setOutcome({ key: outcomeFor(v.code), detail: v.message || '' });
          return;
        }
        scanTicket = v?.scanTicket;
      }

      const r = await fetch(`${API_URL}/api/attendance/direct-action`, {
        method: 'POST',
        headers: auth,
        body: JSON.stringify({
          worker_id: workerId,
          action,
          ...(scanTicket ? { scan_ticket: scanTicket } : {}),
        }),
      }).then((res) => res.json());
      if (r?.success) setOutcome({ key: 'success', detail: r.message || '' });
      else setOutcome({ key: outcomeFor(r?.error), detail: r?.message || '' });
    } catch {
      setOutcome({ key: navigator.onLine === false ? 'offline' : 'verifyError' });
    } finally {
      setBusy(false);
    }
  };

  const pickWorker = (val) => {
    setPickedWorker(val);
    if (!val) return;
    const w = allWorkers.find((x) => String(x.id) === val);
    setWorkerId(val);
    setWorkerName(w ? `${w.first_name} ${w.last_name}` : '');
    setOutcome(null);
  };

  const dismissOutcome = () => { setOutcome(null); setScanResult(null); setWorkerState(null); };
  const rescan = () => { dismissOutcome(); startCamera(); };

  /* ── Renders ─────────────────────────────────────────────────────────────── */

  if (phase === 'init') {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', gap: 2 }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (phase === 'error') {
    return (
      <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <OutcomePanel
            outcomeKey="verifyError" detail={errorMsg} t={t}
            onRetry={() => window.location.reload()}
            onRescan={() => window.location.reload()}
            onDismiss={() => window.location.reload()}
          />
        </Box>
      </Box>
    );
  }

  const needsIdentity = !workerId && allWorkers.length > 0;
  // Vetëm kur serveri e ka thënë shprehimisht "jo". `null` (ende s'dihet) e lë
  // ekranin ashtu siç është.
  const mobileOff = mobileAllowed === false;

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2, py: 1.5, bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider',
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        {onBack && (
          <IconButton size="small" onClick={onBack} aria-label={t('common.back', 'Kthehu')}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <QrCode2Icon sx={{ color: 'primary.main' }} />
        <Typography sx={{ fontWeight: 800, fontSize: '1rem', flex: 1 }}>
          {t('workerScan.title')}
        </Typography>
        {workerName && (
          <Stack direction="row" spacing={0.625} alignItems="center" sx={{ maxWidth: 170, minWidth: 0 }}>
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
              {workerName}
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Shiriti offline — gjendje e përhershme, jo vetëm mesazh pas dështimit */}
      {!online && (
        <Stack
          role="status" aria-live="polite"
          direction="row" spacing={1} alignItems="center"
          sx={{ px: 2, py: 1, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}
        >
          <CloudOffRoundedIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
            {t('workerScan.offlineBar')}
          </Typography>
        </Stack>
      )}

      {/* Tabs */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); if (v !== 0) stopCamera(); }}
          variant="fullWidth"
          aria-label={t('workerScan.title')}
        >
          <Tab label={t('workerScan.tabScan')} sx={{ fontWeight: 700 }} />
          <Tab label={t('workerScan.tabDirect')} sx={{ fontWeight: 700 }} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, gap: 2 }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>

          {remote?.allowed && (
            <RemoteWorkPanel
              remote={remote} actions={ACTIONS} busy={busy} online={online}
              onAction={handleRemoteAction} t={t}
            />
          )}

          {needsIdentity && (
            <IdentityPicker workers={allWorkers} value={pickedWorker} onChange={pickWorker} t={t} />
          )}

          {/* ── TAB 0: Skano QR-në e kiosku ── */}
          {tab === 0 && (
            <>
              {/* Pamja e kamerës */}
              <Box
                sx={{
                  width: '100%', borderRadius: 3, overflow: 'hidden',
                  border: 2, borderColor: 'primary.main', bgcolor: 'common.black',
                  minHeight: scanning ? 280 : 0,
                  display: scanning ? 'block' : 'none',
                }}
              >
                <div id="qr-reader" style={{ width: '100%' }} />
              </Box>

              {scanning && (
                <Stack alignItems="center" spacing={1.25} sx={{ mt: 2 }}>
                  <Typography role="status" sx={{ fontSize: '0.82rem', color: 'text.secondary', textAlign: 'center' }}>
                    {t('workerScan.aimCamera')}
                  </Typography>
                  <Button
                    variant="outlined" size="small" color="inherit"
                    startIcon={<StopIcon />} onClick={stopCamera}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('workerScan.stop')}
                  </Button>
                </Stack>
              )}

              {busy && (
                <Stack alignItems="center" spacing={1.25} sx={{ py: 3 }} role="status" aria-live="polite">
                  <CircularProgress size={30} />
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {t('workerScan.verifying')}
                  </Typography>
                </Stack>
              )}

              {/* Kanali i fikur — asnjë veprim prezence nga telefoni.
                  NUK është `display:none` mbi një buton që ende punon: butoni
                  nuk montohet fare, dhe në vend të tij shfaqet arsyeja. Përndryshe
                  punëtori do ta zbulonte ndalesën vetëm pasi ka skanuar. */}
              {mobileOff && !busy && (
                <ChannelDisabledNotice t={t} />
              )}

              {/* Gjendja "gati për skanim" */}
              {!mobileOff && !scanning && !busy && !workerState && (
                <Stack spacing={2}>
                  <Box sx={{ textAlign: 'center', pt: 2 }}>
                    <Box
                      sx={{
                        width: 84, height: 84, borderRadius: '50%', mx: 'auto', mb: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'primary.soft', color: 'primary.main',
                        border: 1, borderColor: alpha(theme.palette.primary.main, 0.25),
                      }}
                    >
                      <CameraAltIcon sx={{ fontSize: 38 }} />
                    </Box>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, mb: 0.75 }}>
                      {t('workerScan.scanKioskQr')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 2.5, px: 1 }}>
                      {t('workerScan.scanInstruction')}
                    </Typography>
                    <Button
                      variant="contained" size="large" fullWidth
                      startIcon={<CameraAltIcon />}
                      onClick={startCamera}
                      disabled={!online || !workerId}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2.5, py: 1.5, fontSize: '0.95rem' }}
                    >
                      {t('workerScan.openCamera')}
                    </Button>
                    {!workerId && !needsIdentity && (
                      <Typography sx={{ fontSize: '0.76rem', color: 'warning.dark', mt: 1.25 }}>
                        {t('workerScan.noIdentityHint')}
                      </Typography>
                    )}
                  </Box>

                  <VerificationNote t={t} />
                </Stack>
              )}

              {/* Zgjedhja e veprimit kur punëtori është "duke punuar" */}
              {!mobileOff && workerState && !busy && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ p: 1.75, borderRadius: 3, bgcolor: 'primary.soft', border: 1, borderColor: alpha(theme.palette.primary.main, 0.28), mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                        {workerState.worker_name}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.5 }}>
                      {t('workerScan.atKiosk', { kiosk: workerState.kiosk_name })}
                      {workerState.work_minutes_so_far > 0 && (
                        <>
                          {' · '}
                          <Box component="span" className="tabular" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {Math.floor(workerState.work_minutes_so_far / 60)}h {workerState.work_minutes_so_far % 60}min
                          </Box>
                        </>
                      )}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 1.5, textAlign: 'center' }}>
                    {t('workerScan.whatToDo')}
                  </Typography>
                  <Stack spacing={1.25}>
                    {ACTIONS.filter((b) => workerState.allowed_actions?.includes(b.action)).map((b) => (
                      <Button
                        key={b.action} variant="contained" fullWidth
                        disabled={busy || !online}
                        onClick={() => handleKioskAction(b.action)}
                        startIcon={b.icon}
                        sx={{
                          bgcolor: b.color, color: '#fff',
                          '&:hover': { bgcolor: b.color, filter: 'brightness(0.9)' },
                          textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.35, fontSize: '0.95rem',
                        }}
                      >
                        {b.label}
                      </Button>
                    ))}
                  </Stack>
                  <Button
                    fullWidth variant="outlined" size="small" onClick={rescan}
                    sx={{ mt: 2, textTransform: 'none' }}
                  >
                    {t('workerScan.scanAgain')}
                  </Button>
                </Box>
              )}
            </>
          )}

          {/* ── TAB 1: Aksion direkt (pa kiosk) ── */}
          {tab === 1 && mobileOff && <ChannelDisabledNotice t={t} />}
          {tab === 1 && !mobileOff && (
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {ACTIONS.map((b) => (
                  <Button
                    key={b.action} variant="contained" fullWidth
                    disabled={busy || !workerId || !online}
                    onClick={() => handleDirectAction(b.action)}
                    startIcon={!busy ? b.icon : undefined}
                    sx={{
                      bgcolor: b.color, color: '#fff',
                      '&:hover': { bgcolor: b.color, filter: 'brightness(0.9)' },
                      textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.35, fontSize: '0.85rem',
                    }}
                  >
                    {busy ? <CircularProgress size={18} color="inherit" /> : b.label}
                  </Button>
                ))}
              </Box>

              {!workerId && !needsIdentity && (
                <Typography sx={{ fontSize: '0.78rem', color: 'warning.dark' }}>
                  {t('workerScan.noIdentityHint')}
                </Typography>
              )}

              <VerificationNote t={t} />
            </Stack>
          )}

          {/* Rezultati — i përbashkët për të dy tabs */}
          {outcome && !busy && (
            <OutcomePanel
              outcomeKey={outcome.key}
              detail={outcome.detail}
              onRescan={rescan}
              onRetry={() => { const wasTab = tab; dismissOutcome(); if (wasTab === 0) startCamera(); }}
              onDismiss={dismissOutcome}
              t={t}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
